import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Eye, Truck, FileText, MapPin, 
  Loader2, Search, Filter, RefreshCw 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    message: '',
    location: '',
    tracking_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { token } = useAuth();

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(term) ||
        order.customer_name.toLowerCase().includes(term) ||
        order.customer_email.toLowerCase().includes(term) ||
        order.customer_phone?.includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.order_status === statusFilter);
    }
    
    setFilteredOrders(filtered);
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setStatusUpdate({
      status: order.order_status,
      message: '',
      location: '',
      tracking_number: order.tracking_number || ''
    });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate.status) {
      toast.error('Please select a status');
      return;
    }

    setUpdating(true);
    try {
      await axios.patch(
        `${API}/orders/${selectedOrder.id}/status`,
        statusUpdate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated!');
      setShowStatusModal(false);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDialog(true);
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`);
      // For now, show invoice data (PDF generation would be next step)
      console.log('Invoice data:', response.data);
      toast.success('Invoice data retrieved - PDF generation coming soon');
    } catch (error) {
      toast.error('Failed to get invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      out_for_delivery: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      returned: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: 'bg-green-100 text-green-800',
      cod_pending: 'bg-orange-100 text-orange-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatPaymentStatus = (status) => {
    if (status === 'cod_pending') return 'COD Pending';
    return status?.charAt(0).toUpperCase() + status?.slice(1);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-orders-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container-custom py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-muted-foreground hover:text-primary">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-3xl font-heading font-bold">Order Management</h1>
                <p className="text-muted-foreground mt-1">View and manage customer orders</p>
              </div>
            </div>
            <Button onClick={fetchOrders} variant="outline" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="container-custom py-6">
        <div className="bg-white rounded-2xl p-6 shadow-card mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search by order ID, name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="order-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">Loading orders...</p>
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Order ID</th>
                    <th className="text-left p-4 font-semibold">Customer</th>
                    <th className="text-left p-4 font-semibold">Amount</th>
                    <th className="text-left p-4 font-semibold">Payment</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-left p-4 font-semibold">Date</th>
                    <th className="text-left p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="border-t border-border hover:bg-muted/20 transition-colors" data-testid={`order-row-${order.id}`}>
                      <td className="p-4">
                        <span className="font-mono text-sm">{order.id.slice(0, 8)}...</span>
                        {order.payment_method === 'cod' && (
                          <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">COD</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{order.customer_name}</p>
                          <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold">₹{order.total_amount.toFixed(2)}</span>
                        {order.discount_amount > 0 && (
                          <p className="text-xs text-green-600">-₹{order.discount_amount.toFixed(2)} discount</p>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`}>
                          {formatPaymentStatus(order.payment_status)}
                        </span>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => openStatusModal(order)}
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(order.order_status)}`}
                          data-testid={`status-btn-${order.id}`}
                        >
                          {order.order_status.replace('_', ' ')}
                        </button>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => viewOrderDetails(order)}
                            variant="ghost"
                            size="sm"
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => downloadInvoice(order.id)}
                            variant="ghost"
                            size="sm"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl shadow-card">
            <p className="text-lg text-muted-foreground">
              {searchTerm || statusFilter !== 'all' ? 'No orders match your search criteria.' : 'No orders yet.'}
            </p>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Update Order Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">New Status</label>
                <Select
                  value={statusUpdate.status}
                  onValueChange={(value) => setStatusUpdate(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="status-update-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="returned">Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(statusUpdate.status === 'shipped' || statusUpdate.status === 'out_for_delivery') && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Tracking Number</label>
                    <Input
                      value={statusUpdate.tracking_number}
                      onChange={(e) => setStatusUpdate(prev => ({ ...prev, tracking_number: e.target.value }))}
                      placeholder="Enter tracking number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Location</label>
                    <Input
                      value={statusUpdate.location}
                      onChange={(e) => setStatusUpdate(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Mumbai Hub"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Message (optional)</label>
                <Input
                  value={statusUpdate.message}
                  onChange={(e) => setStatusUpdate(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Add a note for the customer"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  onClick={handleStatusUpdate} 
                  disabled={updating}
                  className="flex-1 btn-primary"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Status'}
                </Button>
                <Button variant="outline" onClick={() => setShowStatusModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Order Details Dialog */}
      {selectedOrder && showDialog && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Order Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-6" data-testid="order-details-dialog">
              {/* Order Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/30 p-4 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Order ID</p>
                  <p className="font-mono text-sm">{selectedOrder.id.slice(0, 12)}...</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Invoice #</p>
                  <p className="font-semibold">{selectedOrder.invoice_number || 'N/A'}</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Payment Method</p>
                  <p className="font-semibold capitalize">{selectedOrder.payment_method || 'Online'}</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-xl">
                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.order_status)}`}>
                    {selectedOrder.order_status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className="font-semibold mb-3">Customer Information</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-muted-foreground">Name:</span> {selectedOrder.customer_name}</p>
                  <p><span className="text-muted-foreground">Email:</span> {selectedOrder.customer_email}</p>
                  <p><span className="text-muted-foreground">Phone:</span> {selectedOrder.customer_phone}</p>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Shipping Address
                </h3>
                <div className="text-sm bg-muted/30 p-4 rounded-xl">
                  <p>{selectedOrder.shipping_address.street}</p>
                  <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state}</p>
                  <p>{selectedOrder.shipping_address.pincode}</p>
                </div>
              </div>

              {/* Tracking Info */}
              {selectedOrder.tracking_number && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Tracking Information
                  </h3>
                  <p className="text-sm">Tracking #: <span className="font-mono">{selectedOrder.tracking_number}</span></p>
                </div>
              )}

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-3">Order Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-4 p-3 bg-muted/30 rounded-lg">
                      <img src={item.image} alt={item.product_name} className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-grow">
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-muted-foreground">Size: {item.variant_size}</p>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div className="bg-muted/30 p-4 rounded-xl">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>₹{(selectedOrder.subtotal || selectedOrder.total_amount).toFixed(2)}</span>
                  </div>
                  {selectedOrder.discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount ({selectedOrder.coupon_code})</span>
                      <span>-₹{selectedOrder.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>Shipping</span>
                    <span className="text-green-600">Free</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                    <span>Total</span>
                    <span>₹{selectedOrder.total_amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Tracking History */}
              {selectedOrder.tracking_history && selectedOrder.tracking_history.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Tracking History</h3>
                  <div className="space-y-2">
                    {selectedOrder.tracking_history.map((entry, idx) => (
                      <div key={idx} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                        <div>
                          <p className="font-medium">{entry.status}</p>
                          <p className="text-muted-foreground">{entry.message}</p>
                          {entry.location && <p className="text-muted-foreground text-xs">{entry.location}</p>}
                          <p className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminOrders;
